import { Node } from '@tiptap/core';
import { Color } from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import HorizontalRule from '@tiptap/extension-horizontal-rule';
import TiptapImage from '@tiptap/extension-image';
import TiptapLink from '@tiptap/extension-link';
import ListKeymap from '@tiptap/extension-list-keymap';
import { Paragraph } from "@tiptap/extension-paragraph";
import Placeholder from '@tiptap/extension-placeholder';
import TaskItem from '@tiptap/extension-task-item';
import TaskList from '@tiptap/extension-task-list';
import TextAlign from '@tiptap/extension-text-align';
import TextStyle from '@tiptap/extension-text-style';
import TiptapUnderline from '@tiptap/extension-underline';
import Youtube from '@tiptap/extension-youtube';
import StarterKit from '@tiptap/starter-kit';
import { cx } from "class-variance-authority";
import GlobalDragHandle from 'tiptap-extension-global-drag-handle';
import { font_text_varela } from '../../fonts';
import "./editor.css";


const tiptapLink = TiptapLink.configure({
    "HTMLAttributes": {
        rel: "",
        class: "btn"
    }
})

const tiptapImage = TiptapImage.configure({
    allowBase64: true,
    HTMLAttributes: {
        class: cx("rounded-lg border border-muted"),
    },
});


const taskList = TaskList.configure({
    HTMLAttributes: {
        class: cx("not-prose leading-[1em]"),
    },
});
const taskItem = TaskItem.configure({
    HTMLAttributes: {
        class: cx("flex gap-2 items-start"),
    },
    nested: true,
});

const horizontalRule = HorizontalRule.configure({
    HTMLAttributes: {
        class: cx("mt-4 mb-6 border-nfPurple-500 border-t border-muted-foreground"),
    },
});

const starterKit = StarterKit.configure({
    bulletList: {
        HTMLAttributes: {
            class: cx("list-disc list-inside mt-0.5 marker:text-nfBlue-400 [&_p]:inline text-start"),
        },
    },
    orderedList: {
        HTMLAttributes: {
            class: cx("list-decimal list-inside mt-0.5 marker:text-nfBlue-400 [&_p]:inline text-start"),
        },
    },
    listItem: {
        HTMLAttributes: {
            class: cx("leading-7"),
        },
    },
    codeBlock: {
        HTMLAttributes: {
            class: cx("rounded-md bg-gray-700 text-gray-100 border border-nfPurple-900 px-1.5 py-1", font_text_varela.className),
        },
    },
    code: {
        HTMLAttributes: {
            class: cx("rounded-md bg-gray-700 text-gray-100 px-1.5 py-1 ", font_text_varela.className),
            spellcheck: "false",
        },
    },
    horizontalRule: false,
    dropcursor: {
        color: "#DBEAFE",
        width: 4,
    },
    gapcursor: false,
    paragraph: false
});

/** override <p> tso if its empty show as br */
const CustomParagraph = Paragraph.extend({
    renderHTML({ HTMLAttributes, node }) {
        const editMode = this.editor?.isEditable
        //if after trim no string is found (empty / "")
        if (!editMode && !node.textContent.trim()) {
            return ["br", HTMLAttributes]; //render as <br>
        }
        return ["p", HTMLAttributes, 0];
    },
});

const youtube = Youtube.configure({
    HTMLAttributes: {
        class: cx("rounded-lg border border-muted m-auto"),
    },
    "origin": "https://www.nightfall.co.il",
    inline: false,
});

const highlight = Highlight.configure(
    {
        HTMLAttributes: {
            class: cx("rounded-sm px-1"),
        },
        "multicolor": true
    })

const textAlign = TextAlign.configure({
    "types": ['heading', 'paragraph']
})

const placeholder = Placeholder.configure({
    // Use different placeholders depending on the node type:
    placeholder: ({ node }) => {
        switch (node.type.name) {
            case "heading":
                return 'מה הכותרת?';
            case "paragraph":
                return "מה להוסיף פה?";
            case "bulletList":
                return "מה להוסיף בסגנון נקודות?";
            case "taskList":
                return "מה להוסיף בסגנון מטלות?"
            case "codeBlock":
                return "מה להוסיף בסגנון קוד?"
            default:
                return 'הוספת תוכן דינאמי..'
        }

    },
})

export const defaultExtensions = [
    starterKit,
    /**
     *  +/-/* for list
     * 1./2. for numbered list
     * ``` for code block
     * ` for inline code block
     * 
     */
    CustomParagraph,
    tiptapLink, //allow link paste
    tiptapImage, //allow image paste
    taskList, //allow []/[x] adding checkboxes
    taskItem,
    horizontalRule, //hr
    youtube, // allow youtube embed on paste
    TiptapUnderline,
    highlight, // allow ==two equal signs== to mark
    placeholder,
    TextStyle,
    Color,
    ListKeymap,
    GlobalDragHandle,
    textAlign,

    (typeof window === 'undefined')
    && require('./customComponents/CreatorLogo/CreatorLogo_server').CreatorLogo_server
    || require('./customComponents/CreatorLogo/CreatorLogo_client').CreatorLogo_client,

    (typeof window === 'undefined')
    && require('./customComponents/CountDown/CountDown_server').CountDownEditor_server
    || require('./customComponents/CountDown/CountDown_client').CountDownEditor_client
];

export function getDefaultExtensions(cfg: { isServer: boolean }) {
    const extList = defaultExtensions.filter(e => !(e instanceof Node) || !["CountDown", "CreatorLogo"].includes(e.name)); //get a plugin copy without countdown
    if (cfg.isServer) { //if server add server countdown
        extList.push(require('./customComponents/CreatorLogo/CreatorLogo_server').CreatorLogo_server)
        extList.push(require('./customComponents/CountDown/CountDown_server').CountDownEditor_server)

    }
    else { //if client add client countdown
        extList.push(require('./customComponents/CreatorLogo/CreatorLogo_client').CreatorLogo_client)
        extList.push(require('./customComponents/CountDown/CountDown_client').CountDownEditor_client)

    }

    return extList
}